import React, { useEffect, useState } from 'react';
import { Alert, Accordion } from 'react-bootstrap';
import api from '../../api/apiClient';

const UserFaqPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.faqs.listPublic({ audience: 'END_USER', is_published: '1' });
        if (response.data?.success) {
          setFaqs(response.data.data || []);
        } else {
          setError('Failed to load FAQs');
        }
      } catch (err) {
        console.error('UserFaqPage error:', err);
        setError('Failed to load FAQs');
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-6 sm:p-8 md:p-10">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-3xl font-bold text-gray-900 mb-2">Frequently Asked Questions</h1>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full mb-3"></div>
          <p className="text-gray-600 text-base sm:text-lg">
            Answers to common questions for learners using Enable LXP.
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading FAQs...</p>
          </div>
        )}

        {error && !loading && (
          <Alert variant="danger" className="rounded-xl border-2 border-red-200 bg-red-50 text-red-800 font-medium py-4">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!loading && !error && faqs.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-question-circle text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-600 font-medium text-lg">
              No FAQs are available yet. Please check back later.
            </p>
          </div>
        )}

        {!loading && !error && faqs.length > 0 && (
          <div className="space-y-3">
            <Accordion alwaysOpen className="custom-accordion">
              {faqs
                .slice()
                .sort((a, b) => a.position - b.position || new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
                .map((item, index) => (
                  <Accordion.Item 
                    eventKey={String(item.id || index)} 
                    key={item.id || index}
                    className="mb-3 border-2 border-purple-100 rounded-xl overflow-hidden hover:border-purple-300 transition-all duration-200"
                  >
                    <Accordion.Header className="bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 transition-all duration-200 font-semibold text-gray-900 px-4 sm:px-6 py-4">
                      {item.question}
                    </Accordion.Header>
                    <Accordion.Body className="px-4 sm:px-6 py-4 bg-white">
                      <div 
                        className="prose prose-purple max-w-none text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.answer_html || '' }}
                        style={{
                          fontSize: '16px',
                          lineHeight: '1.8'
                        }}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserFaqPage;
